import React, { useState, useEffect } from "react";
import { Download, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import Button from "../button/Button";
import Checkbox from "./Checkbox";
import ExportModal from "./ExportModal";
import { Purchase } from "../../utils/csvParser";

type ExportParams = {
    purchases: Purchase[];
    groupSize: number;
};

type DataTableProps = {
    purchases: Purchase[];
    headers: string[];
    onExport?: (params: ExportParams) => void;
    className?: string;
};

const PAGE_SIZE = 1000;

const DataTable = ({ purchases, headers, onExport, className = "" }: DataTableProps) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [showExportModal, setShowExportModal] = useState(false);
    const [riskLevel, setRiskLevel] = useState<number>(10);
    const [transactionsPerGroup, setTransactionsPerGroup] = useState<number>(250);

    // Reset to first page when data or sort changes
    useEffect(() => {
        setCurrentPage(1);
    }, [purchases, sortColumn, sortDirection]);

    const handleSelectRow = (id: string, checked: boolean) => {
        const newSelected = new Set(selectedIds);
        if (checked) {
            newSelected.add(id);
        } else {
            newSelected.delete(id);
        }
        setSelectedIds(newSelected);
    };

    // Convert column header to key name
    const getColumnKey = (header: string): string => {
        return header.trim().replace(/\s+/g, '_').replace(/[()]/g, '');
    };

    // Compute display headers with exact priority order (no heuristics)
    const { displayHeaders, riskHeaderLabel, approvalHeaderLabel } = React.useMemo(() => {
        const risk = headers.find((h) => h === "RISK SCORE") || null;
        const itemDesc = headers.find((h) => h === "ITEM DESCRIPTION") || null;
        const approval = headers.find((h) => h === "Approval Result" || h === "APPROVAL RESULT") || null;
        const vendor = headers.find((h) => h === "VENDOR") || null;

        // Priority order per requirement:
        // 1) APPROVAL RESULT, 2) RISK SCORE, 3) ITEM DESCRIPTION
        const priority: string[] = [];
        if (approval) priority.push(approval);
        if (risk) priority.push(risk);
        if (itemDesc) priority.push(itemDesc);
        if (vendor) priority.push(vendor);

        const remaining = headers.filter((h) => !priority.includes(h));
        return { displayHeaders: [...priority, ...remaining], riskHeaderLabel: risk, approvalHeaderLabel: approval };
    }, [headers]);

    // Known keys used for rendering behaviors
    // Resolve LINK header case-insensitively, then normalize to a key
    const linkHeaderLabel = React.useMemo(() => headers.find((h) => h.toUpperCase() === "LINK") || null, [headers]);
    const linkHeaderKey = React.useMemo(() => (linkHeaderLabel ? getColumnKey(linkHeaderLabel) : null), [linkHeaderLabel]);

    // Default sort: when Risk Score column exists, set it to descending (5 -> 1)
    useEffect(() => {
        if (sortColumn) return;
        if (!riskHeaderLabel) return;
        const riskKey = getColumnKey(riskHeaderLabel);
        setSortColumn(riskKey);
        setSortDirection('desc');
    }, [riskHeaderLabel, sortColumn]);

    // Handle column sorting
    const handleSort = (header: string) => {
        const columnKey = getColumnKey(header);
        
        if (sortColumn === columnKey) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(columnKey);
            setSortDirection('asc');
        }
    };

    // Sort purchases based on current sort state
    const sortedPurchases = React.useMemo(() => {
        if (!sortColumn) return purchases;

        return [...purchases].sort((a, b) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            const aNum = Number(aValue);
            const bNum = Number(bValue);
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
            }

            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            
            if (sortDirection === 'asc') {
                return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            } else {
                return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
            }
        });
    }, [purchases, sortColumn, sortDirection]);

    // Pagination
    const totalItems = sortedPurchases.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
    const clampedPage = Math.min(currentPage, totalPages);
    const startIndex = (clampedPage - 1) * PAGE_SIZE;
    const endIndex = Math.min(startIndex + PAGE_SIZE, totalItems);
    const pageRows = React.useMemo(() => sortedPurchases.slice(startIndex, endIndex), [sortedPurchases, startIndex, endIndex]);

    // Page-level header checkbox behavior (select only visible rows)
    const selectedInPageCount = React.useMemo(
        () => pageRows.reduce((acc, p) => acc + (selectedIds.has(p.id) ? 1 : 0), 0),
        [pageRows, selectedIds]
    );
    const allInPageSelected = pageRows.length > 0 && selectedInPageCount === pageRows.length;
    const isIndeterminate = selectedInPageCount > 0 && !allInPageSelected;

    const handleSelectAllPage = (checked: boolean) => {
        const newSelected = new Set(selectedIds);
        if (checked) {
            pageRows.forEach(p => newSelected.add(p.id));
        } else {
            pageRows.forEach(p => newSelected.delete(p.id));
        }
        setSelectedIds(newSelected);
    };

    const handlePrevPage = () => {
        setCurrentPage((p) => Math.max(1, p - 1));
    };

    const handleNextPage = () => {
        setCurrentPage((p) => Math.min(totalPages, p + 1));
    };

    const handleExportClick = () => {
        setShowExportModal(true);
    };

    const handleExportAll = () => {
        // Get the risk score key
        const riskKey = riskHeaderLabel ? getColumnKey(riskHeaderLabel) : null;
        
        // Filter purchases by risk level (>= riskLevel)
        const filterByRiskLevel = (purchases: Purchase[]): Purchase[] => {
            if (!riskKey) return purchases;
            return purchases.filter(p => {
                const riskScore = Number(p[riskKey]);
                return !isNaN(riskScore) && riskScore >= riskLevel;
            });
        };
        
        // Get selected or all purchases, then filter by risk level
        const selectedInSortedOrder = sortedPurchases.filter(p => selectedIds.has(p.id));
        const purchasesToExport = selectedInSortedOrder.length > 0 ? selectedInSortedOrder : sortedPurchases;
        const filteredPurchases = filterByRiskLevel(purchasesToExport);
        
        onExport?.({
            purchases: filteredPurchases,
            groupSize: transactionsPerGroup,
        });
        setShowExportModal(false);
    };

    const handleCloseModal = () => {
        setShowExportModal(false);
    };

    // Calculate filtered count for export button
    const filteredExportCount = React.useMemo(() => {
        const riskKey = riskHeaderLabel ? getColumnKey(riskHeaderLabel) : null;
        if (!riskKey) return sortedPurchases.length;
        
        const selectedInSortedOrder = sortedPurchases.filter(p => selectedIds.has(p.id));
        const purchasesToExport = selectedInSortedOrder.length > 0 ? selectedInSortedOrder : sortedPurchases;
        
        return purchasesToExport.filter(p => {
            const riskScore = Number(p[riskKey]);
            return !isNaN(riskScore) && riskScore >= riskLevel;
        }).length;
    }, [sortedPurchases, selectedIds, riskLevel, riskHeaderLabel]);

    return (
        <div className={`w-full ${className}`}>
            {/* Header */}
            <div className="flex mt-[-16px] flex-col gap-4 mb-8">
                <div className="flex w-full items-center justify-between">
                    <div className="flex flex-col gap-2">
                        <h1 className="text-charcoal text-2xl font-bold">Purchases</h1>
                    </div>
                    <Button
                        variant="primary"
                        size="medium"
                        iconLeft={Download}
                        onClick={handleExportClick}
                    >
                        Export
                    </Button>
                </div>
                <div className="text-sm flex items-center gap-4">
                    <span className="text-charcoal font-medium">Risk Scores Explained:</span>
                    <span className="text-muted">1 - Confident Approval</span>
                    <span className="text-muted">10 - Our recommended level for human review</span>
                    <span className="text-muted">25 - Likely Fraudulent Activity</span>
                </div>
            </div>

            {/* Table Container */}
            <div className="w-full overflow-x-auto bg-white rounded-lg border border-border shadow-sm">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="px-6 py-4 text-left w-12 sticky left-0 bg-white z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                                <Checkbox
                                    checked={allInPageSelected}
                                    indeterminate={isIndeterminate}
                                    onChange={handleSelectAllPage}
                                    aria-label="Select all purchases on this page"
                                />
                            </th>
                            {displayHeaders.map((header, index) => {
                                const isRiskScore = header === "RISK SCORE";
                                const columnKey = getColumnKey(header);
                                const isSorted = sortColumn === columnKey;
                                
                                return (
                                    <th 
                                        key={index} 
                                        className={`px-6 py-4 text-left text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-cloud-tint transition-colors ${
                                            isRiskScore 
                                                ? "text-primary font-bold relative" 
                                                : "text-muted"
                                        }`}
                                        style={isRiskScore ? {
                                            borderLeft: "2px dashed #9333EA",
                                            borderRight: "2px dashed #9333EA",
                                            borderTop: "2px dashed #9333EA",
                                            backgroundColor: "#F5F3FF",
                                            borderTopLeftRadius: "12px",
                                            borderTopRightRadius: "12px"
                                        } : {}}
                                        onClick={() => handleSort(header)}
                                    >
                                        <div className="flex items-center gap-2">
                                            {header === approvalHeaderLabel ? "APPROVAL RESULT" : header}
                                            {isSorted ? (
                                                sortDirection === 'asc' ? (
                                                    <ArrowUp size={16} />
                                                ) : (
                                                    <ArrowDown size={16} />
                                                )
                                            ) : (
                                                <ArrowUpDown size={16} className="opacity-30" />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <tr>
                                <td colSpan={displayHeaders.length + 1} className="px-6 py-12 text-center text-muted text-lg">
                                    No purchases to display. Upload a CSV file to get started.
                                </td>
                            </tr>
                        ) : pageRows.map((purchase, index) => {
                            const isSelected = selectedIds.has(purchase.id);
                            const isLastRow = index === pageRows.length - 1;

                                return (
                                <tr
                                    key={purchase.id}
                                    className={`
                                        transition-colors duration-150 group
                                        ${isSelected ? "bg-primary-tintest" : "hover:bg-cloud-tint"}
                                        ${!isLastRow ? "border-b border-border" : ""}
                                    `}
                                >
                                    <td className={`px-6 py-5 sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] transition-colors duration-150 ${isSelected ? "bg-primary-tintest group-hover:bg-primary-tintest" : "bg-white group-hover:bg-cloud-tint"}`}>
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={(checked) => handleSelectRow(purchase.id, checked)}
                                            aria-label={`Select row ${startIndex + index + 1}`}
                                        />
                                    </td>
                                    {displayHeaders.map((header, colIndex) => {
                                        const key = getColumnKey(header);
                                        const value = purchase[key];
                                        const isRiskScore = header === "RISK SCORE";
                                        const isLinkHeader = linkHeaderKey ? key === linkHeaderKey : false;
                                        const isDescriptionHeader = header === "ITEM DESCRIPTION";
                                        const rowLinkValue = linkHeaderKey ? purchase[linkHeaderKey] : undefined;
                                        return (
                                            <td 
                                                key={colIndex} 
                                                className={`px-6 py-5 text-base whitespace-nowrap ${
                                                    isRiskScore 
                                                        ? "text-primary font-bold text-center" 
                                                        : "text-charcoal"
                                                }`}
                                                style={isRiskScore ? {
                                                    borderLeft: "2px dashed #9333EA",
                                                    borderRight: "2px dashed #9333EA",
                                                    borderBottom: index === pageRows.length - 1 ? "2px dashed #9333EA" : "none",
                                                    backgroundColor: "#F5F3FF",
                                                    borderBottomLeftRadius: index === pageRows.length - 1 ? "12px" : "0",
                                                    borderBottomRightRadius: index === pageRows.length - 1 ? "12px" : "0"
                                                } : {}}
                                            >
                                                {isLinkHeader && typeof value === 'string' && value ? (
                                                    <a
                                                        href={value}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary underline hover:text-primary-tint"
                                                        aria-label="Open link in new tab"
                                                    >
                                                        {value}
                                                    </a>
                                                ) : isDescriptionHeader && typeof rowLinkValue === 'string' && rowLinkValue ? (
                                                    <a
                                                        href={rowLinkValue}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary underline hover:text-primary-tint"
                                                        aria-label="Open item link in new tab"
                                                    >
                                                        {value || "—"}
                                                    </a>
                                                ) : (
                                                    value || "—"
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-muted">
                    Showing {totalItems === 0 ? 0 : startIndex + 1}–{endIndex} of {totalItems} • Page {clampedPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="default"
                        size="small"
                        iconLeft={ChevronLeft}
                        onClick={handlePrevPage}
                        disabled={clampedPage === 1}
                        aria-label="Previous page"
                        tabIndex={0}
                    >
                        Prev
                    </Button>
                    <Button
                        variant="default"
                        size="small"
                        iconLeft={ChevronRight}
                        onClick={handleNextPage}
                        disabled={clampedPage === totalPages}
                        aria-label="Next page"
                        tabIndex={0}
                    >
                        Next
                    </Button>
                </div>
            </div>

            {/* Footer with print icon */}
            <div className="mt-6 flex justify-end">
                <button 
                    className="p-2 hover:bg-cloud-tint rounded-md transition-colors"
                    aria-label="Print table"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                </button>
            </div>

            {/* Export Modal */}
            <ExportModal
                isOpen={showExportModal}
                onClose={handleCloseModal}
                onExport={handleExportAll}
                totalItems={filteredExportCount}
                riskLevel={riskLevel}
                onRiskLevelChange={setRiskLevel}
                transactionsPerGroup={transactionsPerGroup}
                onTransactionsPerGroupChange={setTransactionsPerGroup}
            />
        </div>
    );
};

export default DataTable;